import {
    REPORT_LIST_DEFAULT_TYPE,
    SEARCH_SCOPE_DEFAULT,
    REPORT_LIST_TYPE_OPTIONS,
    SEARCH_SCOPE_OPTIONS,
} from "./constants";

export interface ActiveFilterState {
    searchFilter: string;
    reportType: string;
    searchScope: string;
    favoritesOnly?: boolean;
}

/**
 * Counts how many filter fields differ from their default values.
 * Order and columnOrder are excluded — they are persistent user preferences, not clearable filters.
 * Treats falsy/undefined inputs defensively — returns 0 for invalid state.
 */
export const countActiveFilters = (state?: ActiveFilterState | null): number => {
    if (!state) return 0;

    let count = 0;

    if ((state.searchFilter ?? "") !== "") count++;
    if ((state.reportType ?? REPORT_LIST_DEFAULT_TYPE) !== REPORT_LIST_DEFAULT_TYPE) count++;
    if ((state.searchScope ?? SEARCH_SCOPE_DEFAULT) !== SEARCH_SCOPE_DEFAULT) count++;
    if (state.favoritesOnly === true) count++;

    return count;
};

/**
 * Removes diacritical marks (accents) from a string for comparison purposes.
 * e.g. "andréia" → "andreia", "café" → "cafe"
 */
const removeDiacritics = (str: string): string =>
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * Determines whether a folder card should show the "Contém resultados da busca" badge.
 *
 * Rules:
 * - If searchScope is "folders" → false (folders only appear when name matches)
 * - If searchFilter is non-empty:
 *   - If searchScope is "reports" → true
 *   - If searchScope is "all" → true only if folder name does NOT contain the search term (accent-insensitive)
 * - If reportType is non-default → true (folder contains reports of the filtered type)
 * - Otherwise → false
 */
export const shouldShowContainsResultsBadge = (
    folderName?: string | null,
    searchFilter?: string | null,
    searchScope?: string | null,
    reportType?: string | null,
): boolean => {
    const filter = searchFilter ?? "";
    const scope = searchScope ?? SEARCH_SCOPE_DEFAULT;
    const type = reportType ?? REPORT_LIST_DEFAULT_TYPE;
    const name = folderName ?? "";

    if (scope === "folders") return false;

    const hasActiveSearch = filter !== "";
    const hasActiveType = type !== REPORT_LIST_DEFAULT_TYPE;

    // No active filters at all → no badge
    if (!hasActiveSearch && !hasActiveType) return false;

    // Report type filter active → folder contains filtered results
    if (hasActiveType && !hasActiveSearch) return true;

    // Search active with scope "reports" → folder contains matching reports
    if (hasActiveSearch && scope === "reports") return true;

    // Search active with scope "all" → badge only if folder name doesn't match the search
    if (hasActiveSearch && scope === "all") {
        const normalizedName = removeDiacritics(name.toLowerCase());
        const normalizedFilter = removeDiacritics(filter.toLowerCase());
        const nameMatches = normalizedName.includes(normalizedFilter);
        // If name doesn't match but folder appears → it's because of contained reports
        // If type filter is also active → always show (folder has filtered reports)
        return !nameMatches || hasActiveType;
    }

    return false;
};

/**
 * Determines whether a folder card should show the "Contém favoritos" badge.
 *
 * When the favorites-only filter is active, the backend flags every folder that
 * appears purely because it is an ancestor on the path to a favorited item nested
 * inside it (`contains_favorites`). The badge marks those folders at every level —
 * mirroring "Contém resultados da busca" for search.
 */
export const shouldShowContainsFavoritesBadge = (
    favoritesOnly?: boolean | null,
    containsFavorites?: boolean | null,
): boolean => {
    return favoritesOnly === true && containsFavorites === true;
};

/**
 * Resolves a filter key + value pair to a human-readable label for display in filter tags.
 *
 * - "reportType" → looks up the label from REPORT_LIST_TYPE_OPTIONS
 * - "searchScope" → looks up the label from SEARCH_SCOPE_OPTIONS
 * - Unknown keys or unmatched values → returns the raw value
 */
export const getFilterTagLabel = (
    key: string,
    value?: string | null,
): string => {
    if (!value) return "";

    if (key === "reportType") {
        const option = REPORT_LIST_TYPE_OPTIONS.find((opt) => opt.value === value);
        return option?.label ?? value;
    }

    if (key === "searchScope") {
        const option = SEARCH_SCOPE_OPTIONS.find((opt) => opt.value === value);
        return option?.label ?? value;
    }

    return value;
};
